import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Tabs, TabItem } from "@/components/Tabs";
import { DatePicker } from "@/components/DatePicker";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { Select, SelectOption } from "@/components/Select";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { clientApi, tenderApi, studyApi } from "@/lib/api";
import {
    Client,
    PaymentMethodItem,
    Tender,
    TenderClientType,
    TenderStatus,
    TenderWorkType,
    ClassificationGroup,
    ClassificationSubgroup,
    ClassificationCategory,
    TenderClassificationItem,
} from "@/pages/Tenders/types";
import { QuickClientModal } from "@/pages/Tenders/QuickClientModal";
import { today } from "@/utils/format";
import { useClientLookup } from "@/hooks/useClientLookup";
import { apiFieldErrors } from "@/utils/apiError";

interface TenderFormModalProps {
    isOpen: boolean;
    tender: Tender | null;
    onClose: () => void;
    onSaved?: (savedTender: Tender) => void;
    onSuccess?: (savedTender: Tender) => void;
    isStudy?: boolean;
}

type YesNo = "yes" | "no";
type TabType = "general" | "work" | "economic" | "extra";

export const TenderFormModal: React.FC<TenderFormModalProps> = ({
    isOpen,
    tender,
    onClose,
    onSaved,
    onSuccess,
    isStudy = false,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [quickClientOpen, setQuickClientOpen] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<TabType>("general");
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Form fields
    const [code, setCode] = useState<string>("");
    const [referenceCode, setReferenceCode] = useState<string>("");
    const [clientId, setClientId] = useState<number | null>(null);
    const clientLookup = useClientLookup(clientId, isOpen);
    const clients = clientLookup.items;
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [status, setStatus] = useState<TenderStatus>("decision_pending");
    const [submissionDate, setSubmissionDate] = useState<string>("");
    const [submissionMode, setSubmissionMode] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [streetNumber, setStreetNumber] = useState<string>("");
    const [postalCode, setPostalCode] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [countryId, setCountryId] = useState<number | null>(66);
    const [provinceId, setProvinceId] = useState<number | null>(null);
    const [countries, setCountries] = useState<any[]>([]);
    const [clientTypes, setClientTypes] = useState<TenderClientType[]>([]);
    const [workTypes, setWorkTypes] = useState<TenderWorkType[]>([]);
    const [clientTypeId, setClientTypeId] = useState<number | null>(null);
    const [workTypeId, setWorkTypeId] = useState<number | null>(null);
    const [budget, setBudget] = useState<string>("");
    const [valuationCriteria, setValuationCriteria] = useState<string>("");
    const [deadlinePeriod, setDeadlinePeriod] = useState<string>("");
    const [deadlineUnit, setDeadlineUnit] = useState<
        "days" | "weeks" | "months"
    >("days");
    const [designer, setDesigner] = useState<string>("");
    const [annuities, setAnnuities] = useState<string>("");
    const [observations, setObservations] = useState<string>("");
    const [cancellationReason, setCancellationReason] = useState<string>("");
    const [isUte, setIsUte] = useState<boolean>(false);
    const [generalExpenses, setGeneralExpenses] = useState<string>("");
    const [industrialProfit, setIndustrialProfit] = useState<string>("");
    const [vat, setVat] = useState<string>("21");
    const [provisionalGuarantee, setProvisionalGuarantee] =
        useState<string>("");
    const [definitiveGuarantee, setDefinitiveGuarantee] = useState<string>("");
    const [isClosedPrice, setIsClosedPrice] = useState<boolean>(false);
    const [paymentMethod, setPaymentMethod] = useState<string>("");
    const [subcontracting, setSubcontracting] = useState<string>("");
    const [contractorExpenses, setContractorExpenses] = useState<string>("");
    const [contractorExpensesDesc, setContractorExpensesDesc] =
        useState<string>("");

    const [provinces, setProvinces] = useState<any[]>([]);
    const [classificationGroups, setClassificationGroups] = useState<
        ClassificationGroup[]
    >([]);
    const [classificationCategories, setClassificationCategories] = useState<
        ClassificationCategory[]
    >([]);
    const [selectedClassifications, setSelectedClassifications] = useState<
        TenderClassificationItem[]
    >([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>(
        []
    );

    useEffect(() => {
        if (isOpen) {
            setActiveTab("general");
            clientApi.countries().then((res: any) => {
                const list = res?.data || [];
                setCountries(list);
            });
            clientApi
                .provinces()
                .then((res: any) => setProvinces(res?.data || []));
            loadTypes();
            if (tender) {
                setCode(tender.code || "");
                setReferenceCode(tender.reference_code || "");
                setClientId(tender.client_id || null);
                setTitle(tender.title || "");
                setDescription(tender.description || "");
                setStatus(
                    (typeof tender.status === "string"
                        ? tender.status
                        : tender.status?.tag ||
                          "decision_pending") as TenderStatus
                );
                setSubmissionDate(
                    tender.submission_date
                        ? tender.submission_date.slice(0, 10)
                        : ""
                );
                setSubmissionMode(tender.submission_mode || "");
                setAddress(tender.address || "");
                setStreetNumber(tender.street_number || "");
                setPostalCode(tender.postal_code || "");
                setCity(tender.city || "");
                const cId =
                    tender.country_id ??
                    (tender.country && typeof tender.country === "object"
                        ? tender.country.id
                        : null);
                setCountryId(cId ?? 66);
                const pId =
                    tender.province_id ??
                    (tender.province && typeof tender.province === "object"
                        ? tender.province.id
                        : null);
                setProvinceId(pId ?? null);
                setClientTypeId(
                    tender.client_type_id ?? tender.client_type?.id ?? null
                );
                setWorkTypeId(
                    tender.project_type_id ??
                        tender.work_type_id ??
                        tender.project_type?.id ??
                        tender.work_type?.id ??
                        null
                );
                setBudget(
                    tender.budget !== null && tender.budget !== undefined
                        ? String(tender.budget)
                        : ""
                );
                setValuationCriteria(tender.valuation_criteria || "");
                setDeadlinePeriod(
                    tender.deadline_period !== null &&
                        tender.deadline_period !== undefined
                        ? String(tender.deadline_period)
                        : ""
                );
                setDeadlineUnit(
                    tender.deadline_unit === "weeks" ||
                        tender.deadline_unit === "months"
                        ? tender.deadline_unit
                        : "days"
                );
                setDesigner(tender.designer || "");
                setAnnuities(tender.annuities || "");
                setObservations(tender.observations || "");
                setCancellationReason(tender.cancellation_reason || "");
                setIsUte(Boolean(tender.is_ute));
                setGeneralExpenses(
                    tender.general_expenses_percentage !== null &&
                        tender.general_expenses_percentage !== undefined
                        ? String(tender.general_expenses_percentage)
                        : ""
                );
                setIndustrialProfit(
                    tender.industrial_profit_percentage !== null &&
                        tender.industrial_profit_percentage !== undefined
                        ? String(tender.industrial_profit_percentage)
                        : ""
                );
                setVat(
                    tender.vat_percentage !== null &&
                        tender.vat_percentage !== undefined
                        ? String(tender.vat_percentage)
                        : "21"
                );
                setProvisionalGuarantee(
                    tender.provisional_guarantee_percentage !== null &&
                        tender.provisional_guarantee_percentage !== undefined
                        ? String(tender.provisional_guarantee_percentage)
                        : ""
                );
                setDefinitiveGuarantee(
                    tender.definitive_guarantee_percentage !== null &&
                        tender.definitive_guarantee_percentage !== undefined
                        ? String(tender.definitive_guarantee_percentage)
                        : ""
                );
                setIsClosedPrice(Boolean(tender.is_closed_price));
                setPaymentMethod(tender.payment_method || "");
                setSubcontracting(
                    tender.subcontracting_percentage !== null &&
                        tender.subcontracting_percentage !== undefined
                        ? String(tender.subcontracting_percentage)
                        : ""
                );
                setContractorExpenses(
                    tender.contractor_expenses !== null &&
                        tender.contractor_expenses !== undefined
                        ? String(tender.contractor_expenses)
                        : ""
                );
                setContractorExpensesDesc(
                    tender.contractor_expenses_description || ""
                );
                if (
                    tender.classifications &&
                    Array.isArray(tender.classifications)
                ) {
                    setSelectedClassifications(
                        tender.classifications.map((c: any) => ({
                            group_id: c.group_id ?? null,
                            subgroup_id: c.subgroup_id ?? null,
                            category_id: c.category_id ?? null,
                        }))
                    );
                } else {
                    setSelectedClassifications([]);
                }
            } else {
                setCode("");
                setReferenceCode("");
                setClientId(null);
                setTitle("");
                setDescription("");
                setStatus("decision_pending");
                setSubmissionDate(today());
                setSubmissionMode("");
                setAddress("");
                setStreetNumber("");
                setPostalCode("");
                setCity("");
                setCountryId(66);
                setProvinceId(null);
                setClientTypeId(null);
                setWorkTypeId(null);
                setBudget("");
                setValuationCriteria("");
                setDeadlinePeriod("");
                setDeadlineUnit("days");
                setDesigner("");
                setAnnuities("");
                setObservations("");
                setCancellationReason("");
                setIsUte(false);
                setGeneralExpenses("");
                setIndustrialProfit("");
                setVat("21");
                setProvisionalGuarantee("");
                setDefinitiveGuarantee("");
                setIsClosedPrice(false);
                setPaymentMethod("");
                setSubcontracting("");
                setContractorExpenses("");
                setContractorExpensesDesc("");
                setSelectedClassifications([]);
            }
        }
    }, [isOpen, tender]);

    const loadTypes = async (): Promise<void> => {
        try {
            const [cRes, wRes, cgRes, ccRes, pmRes] = await Promise.allSettled([
                tenderApi.clientTypes(),
                tenderApi.workTypes(),
                tenderApi.classificationGroups(),
                tenderApi.classificationCategories(),
                tenderApi.paymentMethods(),
            ]);

            if (cgRes.status === "fulfilled" && cgRes.value) {
                const data = cgRes.value.data || cgRes.value;
                if (Array.isArray(data)) {
                    setClassificationGroups(data);
                }
            }
            if (ccRes.status === "fulfilled" && ccRes.value) {
                const data = ccRes.value.data || ccRes.value;
                if (Array.isArray(data)) {
                    setClassificationCategories(data);
                }
            }
            if (cRes.status === "fulfilled" && cRes.value) {
                const data = cRes.value.data || cRes.value;
                if (Array.isArray(data)) {
                    setClientTypes(data);
                }
            }
            if (wRes.status === "fulfilled" && wRes.value) {
                const data = wRes.value.data || wRes.value;
                if (Array.isArray(data)) {
                    setWorkTypes(data);
                }
            }
            if (pmRes.status === "fulfilled" && pmRes.value) {
                const data = pmRes.value.data || pmRes.value;
                if (Array.isArray(data)) {
                    setPaymentMethods(data);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getClassificationLabel = (
        item?: { tag?: string | null; name: string } | null
    ): string => {
        if (!item) {
            return "";
        }

        if (item.tag) {
            const key = `classification.${item.tag}`;
            const translated = t(key);
            if (translated && translated !== key) {
                return translated;
            }
        }

        return item.name || "";
    };

    const groupOptions: SelectOption<number>[] = useMemo(() => {
        return classificationGroups.map((g: ClassificationGroup) => ({
            value: g.id,
            label: getClassificationLabel(g),
        }));
    }, [classificationGroups, t]);

    const getSubgroupOptions = (
        groupId: number | null
    ): SelectOption<number>[] => {
        if (!groupId) {
            return [];
        }

        const group = classificationGroups.find(
            (g: ClassificationGroup) => g.id === groupId
        );
        if (!group || !group.subgroups) {
            return [];
        }

        return group.subgroups
            .filter(
                (s: ClassificationSubgroup) =>
                    s.code !== "ALL" &&
                    s.name.toLowerCase() !== "todos" &&
                    !s.tag?.endsWith("_all")
            )
            .map((s: ClassificationSubgroup) => ({
                value: s.id,
                label: getClassificationLabel(s),
            }));
    };

    const categoryOptions: SelectOption<number>[] = useMemo(() => {
        return classificationCategories.map((c: ClassificationCategory) => ({
            value: c.id,
            label: getClassificationLabel(c),
        }));
    }, [classificationCategories, t]);

    const handleAddClassification = (): void => {
        setSelectedClassifications((prev) => [
            ...prev,
            { group_id: null, subgroup_id: null, category_id: null },
        ]);
    };

    const handleRemoveClassification = (index: number): void => {
        setSelectedClassifications((prev) =>
            prev.filter((_, i) => i !== index)
        );
    };

    const handleClearClassifications = (): void => {
        setSelectedClassifications([]);
    };

    const handleUpdateClassification = (
        index: number,
        field: "group_id" | "subgroup_id" | "category_id",
        value: number | null
    ): void => {
        setSelectedClassifications((prev) => {
            const updated = [...prev];
            const current = { ...updated[index] };

            switch (field) {
                case "group_id":
                    current.group_id = value;
                    current.subgroup_id = null;
                    break;
                case "subgroup_id":
                    current.subgroup_id = value;
                    break;
                case "category_id":
                    current.category_id = value;
                    break;
            }

            updated[index] = current;
            return updated;
        });
    };

    const selectedCountry = countries.find((c: any) => c.id === countryId);
    const isSpain: boolean = selectedCountry
        ? Boolean(
              selectedCountry.is_spain ||
                  selectedCountry.name?.toLowerCase() === "españa" ||
                  selectedCountry.code === "ES"
          )
        : countryId === 66 || !countryId;

    const handleCountryChange = (val: number | null): void => {
        const newId = val || null;
        setCountryId(newId);
        setFormErrors((prev) => ({ ...prev, countryId: "" }));
        const c = countries.find((item: any) => item.id === newId);
        const isCountrySpain = c
            ? Boolean(
                  c.is_spain ||
                      c.name?.toLowerCase() === "españa" ||
                      c.code === "ES"
              )
            : newId === 66;
        if (!isCountrySpain) {
            setProvinceId(null);
        }
    };

    const handleSubmit = async (): Promise<void> => {
        const errors: Record<string, string> = {};
        const missingFields: string[] = [];

        if (!submissionDate) {
            errors.submissionDate = t("common.required");
            missingFields.push(t("tender.field_submission_date"));
        }
        if (!clientId) {
            errors.clientId = t("common.required");
            missingFields.push(t("tender.field_client"));
        }
        if (!clientTypeId) {
            errors.clientTypeId = t("common.required");
            missingFields.push(t("tender.field_client_type"));
        }
        if (!title.trim()) {
            errors.title = t("common.required");
            missingFields.push(t("tender.field_title"));
        }
        if (!workTypeId) {
            errors.workTypeId = t("common.required");
            missingFields.push(t("tender.field_work_type"));
        }
        if (!description.trim()) {
            errors.description = t("common.required");
            missingFields.push(t("tender.field_description"));
        }
        if (!city.trim()) {
            errors.city = t("common.required");
            missingFields.push(t("tender.field_city"));
        }
        if (!countryId) {
            errors.countryId = t("common.required");
            missingFields.push(t("tender.field_country"));
        }
        if (isSpain && !provinceId) {
            errors.provinceId = t("common.required");
            missingFields.push(t("tender.field_province"));
        }
        if (!budget) {
            errors.budget = t("common.required");
            missingFields.push(t("tender.field_budget"));
        }
        if (!deadlinePeriod) {
            errors.deadlinePeriod = t("common.required");
            missingFields.push(t("tender.field_deadline"));
        }
        if (!valuationCriteria.trim()) {
            errors.valuationCriteria = t("common.required");
            missingFields.push(t("tender.field_valuation_criteria"));
        }
        if (!industrialProfit) {
            errors.industrialProfit = t("common.required");
            missingFields.push(t("tender.field_industrial_profit"));
        }
        if (!generalExpenses) {
            errors.generalExpenses = t("common.required");
            missingFields.push(t("tender.field_general_expenses"));
        }
        if (!designer.trim()) {
            errors.designer = t("common.required");
            missingFields.push(t("tender.field_designer"));
        }

        if (missingFields.length > 0) {
            setFormErrors(errors);
            showError(
                <div className="flex flex-col gap-1.5 text-left">
                    <span className="text-sm font-bold">
                        {t("common.required_fields_title")}
                    </span>
                    <ul className="list-disc pl-4 opacity-90 text-xs font-medium space-y-0.5">
                        {missingFields.map((f, i) => (
                            <li key={i}>{f}</li>
                        ))}
                    </ul>
                </div>
            );

            // Auto-switch to the tab that contains the first error if it's not the current one
            const generalHasError = [
                "submissionDate",
                "clientId",
                "clientTypeId",
                "title",
                "workTypeId",
                "description",
            ].some((k) => errors[k]);
            const workHasError = ["city", "countryId", "provinceId"].some(
                (k) => errors[k]
            );
            const economicHasError = [
                "budget",
                "deadlinePeriod",
                "valuationCriteria",
                "industrialProfit",
                "generalExpenses",
            ].some((k) => errors[k]);
            const otherHasError = ["designer"].some((k) => errors[k]);

            if (generalHasError && activeTab !== "general") {
                setActiveTab("general");
            } else {
                if (workHasError && activeTab !== "work") {
                    setActiveTab("work");
                } else {
                    if (economicHasError && activeTab !== "economic") {
                        setActiveTab("economic");
                    } else {
                        if (otherHasError && activeTab !== "extra") {
                            setActiveTab("extra");
                        }
                    }
                }
            }

            return;
        }

        setFormErrors({});
        setIsSubmitting(true);
        try {
            const statusMap: Record<string, number> = {
                decision_pending: 1,
                study_pending: 2,
                accepted: 3,
                rejected: 4,
                historical: 5,
            };

            const payload: Record<string, unknown> = {
                title: title.trim(),
                client_id: clientId,
                project_type_id: workTypeId,
                description: description.trim() || null,
                status_id: statusMap[status] ?? 1,
                submission_date: submissionDate || null,
                submission_mode: submissionMode.trim() || null,
                address: address.trim() || null,
                street_number: streetNumber.trim() || null,
                postal_code: postalCode.trim() || null,
                city: city.trim() || null,
                country_id: countryId || null,
                province_id: isSpain ? provinceId || null : null,
                budget: budget ? parseFloat(budget) : null,
                valuation_criteria: valuationCriteria.trim() || null,
                deadline_period: deadlinePeriod
                    ? parseInt(deadlinePeriod, 10)
                    : null,
                deadline_unit_id:
                    deadlineUnit === "months"
                        ? 3
                        : deadlineUnit === "weeks"
                          ? 2
                          : 1,
                deadline_unit: deadlineUnit,
                designer: designer.trim() || null,
                annuities: annuities.trim() || null,
                observations: observations.trim() || null,
                cancellation_reason: cancellationReason.trim() || null,
                is_ute: isUte,
                general_expenses_percentage: generalExpenses
                    ? parseFloat(generalExpenses)
                    : null,
                industrial_profit_percentage: industrialProfit
                    ? parseFloat(industrialProfit)
                    : null,
                vat_percentage: vat ? parseFloat(vat) : null,
                provisional_guarantee_percentage: provisionalGuarantee
                    ? parseFloat(provisionalGuarantee)
                    : null,
                definitive_guarantee_percentage: definitiveGuarantee
                    ? parseFloat(definitiveGuarantee)
                    : null,
                is_closed_price: isClosedPrice,
                payment_method: paymentMethod.trim() || null,
                payment_method_id:
                    paymentMethods.find(
                        (pm: PaymentMethodItem) =>
                            pm.name === paymentMethod.trim()
                    )?.id || null,
                subcontracting_percentage: subcontracting
                    ? parseFloat(subcontracting)
                    : null,
                contractor_expenses: contractorExpenses
                    ? parseFloat(contractorExpenses)
                    : null,
                contractor_expenses_description:
                    contractorExpensesDesc.trim() || null,
                classifications: selectedClassifications.filter(
                    (c) => c.group_id && c.subgroup_id && c.category_id
                ),
            };

            if (code.trim()) {
                payload.code = code.trim();
            }

            let saved: Tender;
            if (tender) {
                const res = isStudy
                    ? await studyApi.update(tender.id, payload)
                    : await tenderApi.update(tender.id, payload);
                saved = res.data;
            } else {
                const res = await tenderApi.create(payload);
                saved = res.data;
            }

            showSuccess(
                isStudy ? t("studies.saved_success") : t("tender.saved_success")
            );
            if (onSaved) {
                onSaved(saved);
            } else {
                if (onSuccess) {
                    onSuccess(saved);
                }
            }
            onClose();
        } catch (error) {
            const fieldMap: Record<string, string> = {
                client_id: "clientId",
                client_type_id: "clientTypeId",
                project_type_id: "workTypeId",
                submission_date: "submissionDate",
                country_id: "countryId",
                province_id: "provinceId",
                deadline_period: "deadlinePeriod",
                valuation_criteria: "valuationCriteria",
                general_expenses_percentage: "generalExpenses",
                industrial_profit_percentage: "industrialProfit",
            };
            const fields = Object.fromEntries(
                Object.entries(apiFieldErrors(error)).map(
                    ([field, message]) => [fieldMap[field] || field, message]
                )
            );
            setFormErrors(fields);
            const group: TabType = [
                "submissionDate",
                "clientId",
                "clientTypeId",
                "title",
                "workTypeId",
                "description",
            ].some((field) => fields[field])
                ? "general"
                : [
                        "city",
                        "countryId",
                        "provinceId",
                        "address",
                        "postalCode",
                    ].some((field) => fields[field])
                  ? "work"
                  : [
                          "budget",
                          "deadlinePeriod",
                          "valuationCriteria",
                          "industrialProfit",
                          "generalExpenses",
                      ].some((field) => fields[field])
                    ? "economic"
                    : "extra";
            if (Object.keys(fields).length) {
                setActiveTab(group);
            }
            showError(
                error instanceof Error ? error.message : t("common.error")
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClientSelect = (val: number): void => {
        const id = val || null;
        setClientId(id);
        if (id) {
            const selectedClient = clients.find((c: Client) => c.id === id);
            if (selectedClient?.client_type_id) {
                handleClientTypeChange(selectedClient.client_type_id);
            }
        }
    };

    const handleClientTypeChange = (ctId: number | null): void => {
        setClientTypeId(ctId);
        if (ctId) {
            const allowedWorkTypes = workTypes.filter(
                (wt: TenderWorkType) =>
                    !wt.client_type_id || wt.client_type_id === ctId
            );
            if (
                workTypeId &&
                !allowedWorkTypes.some(
                    (wt: TenderWorkType) => wt.id === workTypeId
                )
            ) {
                setWorkTypeId(null);
            }
        }
    };

    const clientOptions: SelectOption<number>[] = clients.map((c: Client) => ({
        value: c.id,
        label: c.name + (c.tax_id ? ` (${c.tax_id})` : ""),
    }));

    const clientTypeOptions: SelectOption<number>[] = clientTypes.map(
        (ct: TenderClientType) => ({
            value: ct.id,
            label: ct.name,
        })
    );

    const filteredWorkTypes = clientTypeId
        ? workTypes.filter(
              (wt: TenderWorkType) =>
                  !wt.client_type_id || wt.client_type_id === clientTypeId
          )
        : workTypes;

    const workTypeOptions: SelectOption<number>[] = filteredWorkTypes.map(
        (wt: TenderWorkType) => ({
            value: wt.id,
            label: wt.name,
        })
    );

    const yesNoOptions: SelectOption<YesNo>[] = [
        { value: "yes", label: t("common.yes") },
        { value: "no", label: t("common.no") },
    ];

    const deadlineUnitOptions: SelectOption<"days" | "weeks" | "months">[] = [
        { value: "days", label: t("tender.unit_days") },
        { value: "weeks", label: t("tender.unit_weeks") },
        { value: "months", label: t("tender.unit_months") },
    ];

    const submissionModeOptions: SelectOption<string>[] = useMemo(() => {
        const defaults = [
            { value: "Física", label: t("tender.submission_mode_physical") },
            {
                value: "Telemática",
                label: t("tender.submission_mode_telemetric"),
            },
        ];
        if (
            submissionMode &&
            !defaults.some((d) => d.value === submissionMode)
        ) {
            return [
                { value: submissionMode, label: submissionMode },
                ...defaults,
            ];
        }
        return defaults;
    }, [submissionMode, t]);

    const paymentMethodOptions: SelectOption<string>[] = useMemo(() => {
        const options = paymentMethods.map((pm: PaymentMethodItem) => ({
            value: pm.name,
            label: pm.name,
        }));
        if (
            paymentMethod &&
            !options.some(
                (o: SelectOption<string>) => o.value === paymentMethod
            )
        ) {
            return [{ value: paymentMethod, label: paymentMethod }, ...options];
        }
        return options;
    }, [paymentMethods, paymentMethod]);

    const statusOptions: SelectOption<TenderStatus>[] = [
        {
            value: "decision_pending",
            label: t("tender.status.decision_pending"),
        },
        { value: "study_pending", label: t("tender.status.study_pending") },
        { value: "accepted", label: t("tender.status.accepted") },
        { value: "rejected", label: t("tender.status.rejected") },
        { value: "historical", label: t("tender.status.historical") },
    ];

    const countryOptions: SelectOption<number>[] = useMemo(() => {
        return countries.map((c: any) => ({
            value: c.id,
            label: c.name,
        }));
    }, [countries]);

    const provinceOptions: SelectOption<number>[] = useMemo(() => {
        return provinces.map((p: any) => ({
            value: p.id,
            label: p.name,
        }));
    }, [provinces]);

    const formTabs: TabItem<TabType>[] = useMemo(
        () => [
            { id: "general", label: t("tender.block_general") },
            { id: "work", label: t("tender.block_work") },
            { id: "economic", label: t("tender.block_economic") },
            { id: "extra", label: t("tender.block_extra") },
        ],
        [t]
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={
                    tender
                        ? isStudy
                            ? t("studies.action_edit")
                            : t("tender.edit_tender")
                        : t("tender.new_tender")
                }
                maxWidth="3xl"
                onSubmit={handleSubmit}
                submitLabel={t("tender.save")}
                cancelLabel={t("tender.cancel")}
                isSubmitting={isSubmitting}
            >
                <div className="space-y-4 py-1">
                    {/* Navigation tabs */}
                    <Tabs<TabType>
                        tabs={formTabs}
                        activeTab={activeTab}
                        onChange={(tabId: TabType) => setActiveTab(tabId)}
                        syncUrl={false}
                        size="sm"
                        className="mb-4"
                        subtabs
                        full
                    />

                    {/* Tab 1: General & Client */}
                    {activeTab === "general" && (
                        <div className="space-y-4 pt-1">
                            {tender && (
                                <Input
                                    label={t("tender.field_code")}
                                    value={code}
                                    disabled
                                />
                            )}

                            <DatePicker
                                full
                                required
                                type="date"
                                label={t("tender.field_submission_date")}
                                placeholder={t("tender.field_submission_date")}
                                value={submissionDate || null}
                                onChange={(val) => {
                                    setSubmissionDate(val ?? "");
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        submissionDate: "",
                                    }));
                                }}
                                error={formErrors.submissionDate}
                            />

                            <Select<number>
                                label={t("tender.field_client")}
                                required
                                value={clientId || 0}
                                onChange={(val) => {
                                    handleClientSelect(val);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        clientId: "",
                                    }));
                                }}
                                error={formErrors.clientId}
                                options={clientOptions}
                                onSearchChange={clientLookup.search}
                                searchable
                                clearable
                                full
                                actions={
                                    <Button
                                        variant="custom"
                                        type="button"
                                        onlyicon
                                        size="none"
                                        onClick={() => setQuickClientOpen(true)}
                                        tooltip={t("tender.quick_client_title")}
                                        icon="plus"
                                        className="btn-action-select"
                                    />
                                }
                            />

                            <Select<number>
                                label={t("tender.field_client_type")}
                                required
                                value={clientTypeId || 0}
                                onChange={(val) => {
                                    handleClientTypeChange(val);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        clientTypeId: "",
                                    }));
                                }}
                                error={formErrors.clientTypeId}
                                options={clientTypeOptions}
                                clearable
                                full
                            />

                            <Input
                                label={t("tender.field_title")}
                                required
                                value={title}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    setTitle(e.target.value);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        title: "",
                                    }));
                                }}
                                error={formErrors.title}
                                placeholder={t("tender.field_title")}
                            />

                            {tender && (
                                <Select<TenderStatus>
                                    label={t("tender.field_status")}
                                    value={status}
                                    onChange={(val: TenderStatus) =>
                                        setStatus(val)
                                    }
                                    options={statusOptions}
                                    full
                                />
                            )}

                            <Select<number>
                                label={t("tender.field_work_type")}
                                required
                                value={workTypeId || 0}
                                onChange={(val: number) => {
                                    setWorkTypeId(val || null);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        workTypeId: "",
                                    }));
                                }}
                                error={formErrors.workTypeId}
                                options={workTypeOptions}
                                clearable
                                full
                            />

                            <Textarea
                                label={t("tender.field_description")}
                                required
                                value={description}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    setDescription(e.target.value);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        description: "",
                                    }));
                                }}
                                error={formErrors.description}
                                placeholder={t("tender.field_description")}
                                rows={3}
                                resizable
                                fullscreenEnabled
                            />

                            <Select<string>
                                label={t("tender.field_submission_mode")}
                                value={submissionMode}
                                onChange={(val: string) =>
                                    setSubmissionMode(val)
                                }
                                options={submissionModeOptions}
                                clearable
                                full
                            />
                        </div>
                    )}

                    {/* Tab 2: Work & Location */}
                    {activeTab === "work" && (
                        <div className="space-y-4 pt-1">
                            <Input
                                label={t("tender.field_address")}
                                value={address}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setAddress(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_street_number")}
                                value={streetNumber}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setStreetNumber(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_postal_code")}
                                value={postalCode}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setPostalCode(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_city")}
                                required
                                value={city}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    setCity(e.target.value);
                                    setFormErrors((prev) => ({
                                        ...prev,
                                        city: "",
                                    }));
                                }}
                                error={formErrors.city}
                            />

                            <Select<number>
                                label={t("tender.field_country")}
                                required
                                value={countryId ?? 0}
                                onChange={handleCountryChange}
                                options={countryOptions}
                                searchable
                                clearable
                                full
                                error={formErrors.countryId}
                            />

                            {isSpain && (
                                <Select<number>
                                    label={t("tender.field_province")}
                                    required
                                    value={provinceId ?? 0}
                                    onChange={(val: number) => {
                                        setProvinceId(val || null);
                                        setFormErrors((prev) => ({
                                            ...prev,
                                            provinceId: "",
                                        }));
                                    }}
                                    options={provinceOptions}
                                    searchable
                                    clearable
                                    full
                                    error={formErrors.provinceId}
                                />
                            )}
                        </div>
                    )}

                    {/* Tab 3: Economic & Valuation */}
                    {activeTab === "economic" && (
                        <div className="space-y-4 pt-1">
                            <Input
                                label={t("tender.field_budget")}
                                type="price"
                                required
                                error={formErrors.budget}
                                value={budget}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setBudget(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_deadline")}
                                type="number"
                                required
                                error={formErrors.deadlinePeriod}
                                value={deadlinePeriod}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setDeadlinePeriod(e.target.value)}
                            />

                            <Select<"days" | "weeks" | "months">
                                label={t("tender.field_deadline_unit")}
                                value={deadlineUnit}
                                onChange={(val: "days" | "weeks" | "months") =>
                                    setDeadlineUnit(val)
                                }
                                options={deadlineUnitOptions}
                                full
                            />

                            <Input
                                label={t("tender.field_general_expenses")}
                                type="price"
                                required
                                error={formErrors.generalExpenses}
                                value={generalExpenses}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setGeneralExpenses(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_industrial_profit")}
                                type="price"
                                required
                                error={formErrors.industrialProfit}
                                value={industrialProfit}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setIndustrialProfit(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_vat")}
                                type="percentage"
                                value={vat}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setVat(e.target.value)}
                            />

                            <Textarea
                                label={t("tender.field_valuation_criteria")}
                                required
                                error={formErrors.valuationCriteria}
                                value={valuationCriteria}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => setValuationCriteria(e.target.value)}
                            />

                            <Select<string>
                                label={t("tender.field_payment_method")}
                                value={paymentMethod}
                                onChange={(val: string) =>
                                    setPaymentMethod(val)
                                }
                                options={paymentMethodOptions}
                                clearable
                                full
                            />

                            <Input
                                label={t("tender.field_provisional_guarantee")}
                                type="number"
                                step="0.01"
                                value={provisionalGuarantee}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setProvisionalGuarantee(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_definitive_guarantee")}
                                type="number"
                                step="0.01"
                                value={definitiveGuarantee}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setDefinitiveGuarantee(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_contractor_expenses")}
                                type="price"
                                value={contractorExpenses}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setContractorExpenses(e.target.value)}
                            />

                            <Textarea
                                label={t(
                                    "tender.field_contractor_expenses_desc"
                                )}
                                value={contractorExpensesDesc}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => setContractorExpensesDesc(e.target.value)}
                            />

                            <Select<YesNo>
                                label={t("tender.field_closed_price")}
                                value={isClosedPrice ? "yes" : "no"}
                                onChange={(val: YesNo) =>
                                    setIsClosedPrice(val === "yes")
                                }
                                options={yesNoOptions}
                                full
                            />

                            <Input
                                label={t("tender.field_subcontracting")}
                                type="percentage"
                                value={subcontracting}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setSubcontracting(e.target.value)}
                            />

                            <Input
                                label={t("tender.field_annuities")}
                                value={annuities}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setAnnuities(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Tab 4: Extra (Observations, UTE, Classifications & BC3 Import) */}
                    {activeTab === "extra" && (
                        <div className="space-y-6 pt-1">
                            <Select<YesNo>
                                label={t("tender.field_is_ute")}
                                value={isUte ? "yes" : "no"}
                                onChange={(val: YesNo) =>
                                    setIsUte(val === "yes")
                                }
                                options={yesNoOptions}
                                full
                            />

                            <Input
                                label={t("tender.field_designer")}
                                required
                                error={formErrors.designer}
                                value={designer}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setDesigner(e.target.value)}
                            />

                            <Textarea
                                label={t("tender.field_observations")}
                                value={observations}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => setObservations(e.target.value)}
                            />

                            <Textarea
                                label={t("tender.field_cancellation_reason")}
                                value={cancellationReason}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => setCancellationReason(e.target.value)}
                            />

                            {/* Classifications / Categories block */}
                            <div className="w-full">
                                <div className="flex items-center justify-between mb-1.5 px-0.5 min-h-[18px]">
                                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300">
                                        {t("tender.block_categories")}
                                    </label>
                                    <div className="flex items-end gap-1.5 leading-none ml-auto translate-y-[2px]">
                                        <Button
                                            variant="custom"
                                            type="button"
                                            onlyicon
                                            size="none"
                                            onClick={handleAddClassification}
                                            tooltip={t(
                                                "tender.add_classification"
                                            )}
                                            icon="plus"
                                            className="btn-action-select"
                                        />
                                        {selectedClassifications.length > 0 && (
                                            <Button
                                                variant="custom"
                                                type="button"
                                                onlyicon
                                                size="none"
                                                onClick={
                                                    handleClearClassifications
                                                }
                                                tooltip={t(
                                                    "tender.clear_classifications"
                                                )}
                                                icon="trash"
                                                className="btn-action-select !text-rose-500 hover:!text-rose-700"
                                            />
                                        )}
                                    </div>
                                </div>

                                {selectedClassifications.length === 0 ? (
                                    <div className="w-full min-h-[38px] px-3.5 py-2.5 bg-white dark:bg-dark-box border border-slate-200 dark:border-white/20 rounded-xl text-xs text-slate-400 dark:text-zinc-500 flex items-center shadow-xs">
                                        {t("common.no_results")}
                                    </div>
                                ) : (
                                    <div className="w-full rounded-xl border border-slate-200 dark:border-white/20 bg-white dark:bg-dark-box p-3 shadow-xs space-y-3">
                                        {selectedClassifications.map(
                                            (item, index) => {
                                                const subOptions =
                                                    getSubgroupOptions(
                                                        item.group_id
                                                    );

                                                return (
                                                    <div
                                                        key={index}
                                                        className="p-3.5 rounded-lg border border-slate-200/80 dark:border-white/10 bg-slate-50/60 dark:bg-white/5 space-y-3"
                                                    >
                                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                                                            <div className="md:col-span-4">
                                                                <Select<number>
                                                                    label={t(
                                                                        "tender.classification_groups"
                                                                    )}
                                                                    value={
                                                                        item.group_id ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        val: any
                                                                    ) =>
                                                                        handleUpdateClassification(
                                                                            index,
                                                                            "group_id",
                                                                            val
                                                                                ? Number(
                                                                                      val
                                                                                  )
                                                                                : null
                                                                        )
                                                                    }
                                                                    options={
                                                                        groupOptions
                                                                    }
                                                                    placeholder={t(
                                                                        "tender.select_classification"
                                                                    )}
                                                                    full
                                                                />
                                                            </div>
                                                            <div className="md:col-span-4">
                                                                <Select<number>
                                                                    label={t(
                                                                        "tender.classification_subgroups"
                                                                    )}
                                                                    value={
                                                                        item.subgroup_id ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        val: any
                                                                    ) =>
                                                                        handleUpdateClassification(
                                                                            index,
                                                                            "subgroup_id",
                                                                            val
                                                                                ? Number(
                                                                                      val
                                                                                  )
                                                                                : null
                                                                        )
                                                                    }
                                                                    options={
                                                                        subOptions
                                                                    }
                                                                    placeholder={t(
                                                                        "tender.select_classification"
                                                                    )}
                                                                    disabled={
                                                                        !item.group_id
                                                                    }
                                                                    full
                                                                />
                                                            </div>
                                                            <div className="md:col-span-3">
                                                                <Select<number>
                                                                    label={t(
                                                                        "tender.classification_categories"
                                                                    )}
                                                                    value={
                                                                        item.category_id ??
                                                                        0
                                                                    }
                                                                    onChange={(
                                                                        val: any
                                                                    ) =>
                                                                        handleUpdateClassification(
                                                                            index,
                                                                            "category_id",
                                                                            val
                                                                                ? Number(
                                                                                      val
                                                                                  )
                                                                                : null
                                                                        )
                                                                    }
                                                                    options={
                                                                        categoryOptions
                                                                    }
                                                                    placeholder={t(
                                                                        "tender.select_classification"
                                                                    )}
                                                                    full
                                                                />
                                                            </div>
                                                            <div className="md:col-span-1 flex justify-end pb-1">
                                                                <Button
                                                                    type="button"
                                                                    variant="danger"
                                                                    onlyicon
                                                                    onClick={() =>
                                                                        handleRemoveClassification(
                                                                            index
                                                                        )
                                                                    }
                                                                    icon="trash"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            <QuickClientModal
                isOpen={quickClientOpen}
                onClose={() => setQuickClientOpen(false)}
                onClientCreated={(newClient: Client) => {
                    clientLookup.add(newClient);
                    handleClientSelect(newClient.id);
                }}
            />
        </>
    );
};
